(() => {
  const SELECTOR = 'media-gallery .product-media-container.media-type-image';
  const MIN_SCALE = 1;
  const MAX_SCALE = 4;
  const DOUBLE_TAP_SCALE = 2.25;
  const DOUBLE_TAP_MS = 360;
  const DOUBLE_TAP_DISTANCE = 52;
  const TAP_MOVE_TOLERANCE = 12;
  const TAP_MAX_DURATION = 300;
  const TOUCH_DBLCLICK_GUARD = 750;
  const CLICK_SUPPRESS_MS = 520;
  const DYNAMIC_ZOOM_WIDTH = 3200;
  const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

  const absoluteUrl = (value) => {
    if (!value) return '';
    try { return new URL(value, window.location.href).href; }
    catch (_error) { return String(value); }
  };

  const assetPath = (value) => {
    try { return new URL(value, window.location.href).pathname; }
    catch (_error) { return String(value || '').split('?')[0]; }
  };

  const largestSrcsetUrl = (image) => {
    const srcset = image?.getAttribute?.('srcset') || '';
    let largest = null;
    srcset.split(',').forEach((candidate) => {
      const parts = candidate.trim().split(/\s+/);
      const width = Number.parseInt(parts[1] || '0', 10) || 0;
      if (parts[0] && (!largest || width > largest.width)) largest = { url: parts[0], width };
    });
    return largest?.url || '';
  };

  const resizedShopifyUrl = (value, width) => {
    if (!value) return '';
    try {
      const url = new URL(value, window.location.href);
      if (url.searchParams.has('width') || /shopify|cdn/i.test(url.hostname)) {
        url.searchParams.set('width', String(width));
        return url.href;
      }
      return url.href;
    } catch (_error) {
      return String(value).replace(/_\d+x\d*(\.[a-z0-9]+)(?=\?|$)/i, `_${width}x$1`);
    }
  };

  function initViewport(viewport) {
    if (!viewport || viewport.dataset.localZoomReady === 'true') return;
    const content = viewport.querySelector('.product__media');
    const mediaItem = viewport.closest('.product__media-item');
    if (!content || !mediaItem) return;
    viewport.dataset.localZoomReady = 'true';

    let scale = 1;
    let x = 0;
    let y = 0;
    let startScale = 1;
    let startDistance = 0;
    let startMid = null;
    let panStart = null;
    let tapStart = null;
    let moved = false;
    let lastTap = null;
    let lastTapTimer = 0;
    let lastTouchAt = 0;
    let suppressClickUntil = 0;
    const highResolution = new WeakMap();

    const overlays = () => Array.from(mediaItem.querySelectorAll(':scope > .royal-pop-pdp-gallery__overlay'));
    const zoomImages = () => {
      const images = [];
      const primary = content.querySelector('img');
      if (primary) images.push(primary);
      overlays().forEach((overlay) => {
        if (overlay instanceof HTMLImageElement && !images.includes(overlay)) images.push(overlay);
      });
      return images;
    };

    const highResolutionSource = (image, index) => {
      const current = absoluteUrl(image.currentSrc || image.src || largestSrcsetUrl(image));
      const declared = index === 0 ? absoluteUrl(viewport.dataset.localZoomSrc || '') : '';
      if (declared && current && assetPath(declared) === assetPath(current)) return declared;
      const largest = absoluteUrl(largestSrcsetUrl(image));
      return resizedShopifyUrl(largest || current, DYNAMIC_ZOOM_WIDTH);
    };

    const commitHighResolution = (image, source) => {
      if (!image || !source) return;
      const current = absoluteUrl(image.currentSrc || image.src || '');
      if (current === absoluteUrl(source)) return;
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.src = source;
      image.dataset.localZoomHighResolution = 'true';
    };

    const prepareHighResolution = (commit) => {
      zoomImages().forEach((image, index) => {
        const source = highResolutionSource(image, index);
        if (!source) return;
        let state = highResolution.get(image);
        if (!state || state.source !== source) {
          const preload = new Image();
          state = { source, preload, ready: false, commitRequested: false };
          highResolution.set(image, state);
          preload.decoding = 'async';
          preload.onload = () => {
            state.ready = true;
            if (state.commitRequested) commitHighResolution(image, source);
          };
          preload.src = source;
        }
        if (commit) {
          state.commitRequested = true;
          if (state.ready || state.preload.complete) commitHighResolution(image, source);
        }
      });
    };

    const apply = () => {
      if (scale <= 1.001) {
        scale = 1;
        x = 0;
        y = 0;
        content.style.removeProperty('transform');
        overlays().forEach((el) => el.style.removeProperty('transform'));
        viewport.classList.remove('is-local-zoomed', 'is-local-zoom-panning');
        return;
      }
      const rect = viewport.getBoundingClientRect();
      x = clamp(x, -(rect.width * scale - rect.width), 0);
      y = clamp(y, -(rect.height * scale - rect.height), 0);
      const value = `translate3d(${x}px,${y}px,0) scale(${scale})`;
      content.style.transform = value;
      overlays().forEach((el) => { el.style.transform = value; });
      viewport.classList.add('is-local-zoomed');
    };

    const reset = () => {
      scale = 1;
      x = 0;
      y = 0;
      apply();
    };

    const distance = (a, b) => Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);
    const midpoint = (a, b) => ({ x: (a.clientX + b.clientX) / 2, y: (a.clientY + b.clientY) / 2 });
    const clearLastTap = () => {
      lastTap = null;
      if (lastTapTimer) window.clearTimeout(lastTapTimer);
      lastTapTimer = 0;
    };

    const zoomAt = (clientX, clientY) => {
      if (scale > 1) {
        reset();
        return;
      }
      prepareHighResolution(true);
      scale = DOUBLE_TAP_SCALE;
      const rect = viewport.getBoundingClientRect();
      x = -(clientX - rect.left) * (scale - 1);
      y = -(clientY - rect.top) * (scale - 1);
      apply();
    };

    viewport.addEventListener('touchstart', (event) => {
      lastTouchAt = Date.now();
      moved = false;
      if (event.touches.length === 2) {
        event.preventDefault();
        clearLastTap();
        tapStart = null;
        moved = true;
        prepareHighResolution(true);
        startDistance = distance(event.touches[0], event.touches[1]);
        startScale = scale;
        startMid = midpoint(event.touches[0], event.touches[1]);
        viewport.classList.add('is-local-zoom-panning');
      } else if (event.touches.length === 1) {
        const touch = event.touches[0];
        tapStart = { clientX: touch.clientX, clientY: touch.clientY, time: Date.now() };
        if (scale > 1) {
          panStart = { clientX: touch.clientX, clientY: touch.clientY, x, y };
          viewport.classList.add('is-local-zoom-panning');
        }
      }
    }, { passive: false });

    viewport.addEventListener('touchmove', (event) => {
      lastTouchAt = Date.now();
      if (event.touches.length === 2 && startDistance && startMid) {
        event.preventDefault();
        moved = true;
        const nextMid = midpoint(event.touches[0], event.touches[1]);
        const nextScale = clamp(startScale * (distance(event.touches[0], event.touches[1]) / startDistance), MIN_SCALE, MAX_SCALE);
        const rect = viewport.getBoundingClientRect();
        const localX = startMid.x - rect.left;
        const localY = startMid.y - rect.top;
        const ratio = nextScale / scale;
        x = localX - (localX - x) * ratio + (nextMid.x - startMid.x);
        y = localY - (localY - y) * ratio + (nextMid.y - startMid.y);
        scale = nextScale;
        startMid = nextMid;
        apply();
      } else if (event.touches.length === 1) {
        const touch = event.touches[0];
        if (tapStart && Math.hypot(touch.clientX - tapStart.clientX, touch.clientY - tapStart.clientY) > TAP_MOVE_TOLERANCE) {
          moved = true;
          clearLastTap();
        }
        if (scale > 1 && panStart) {
          event.preventDefault();
          moved = true;
          x = panStart.x + touch.clientX - panStart.clientX;
          y = panStart.y + touch.clientY - panStart.clientY;
          apply();
        }
      }
    }, { passive: false });

    viewport.addEventListener('touchend', (event) => {
      lastTouchAt = Date.now();
      if (event.touches.length < 2) {
        startDistance = 0;
        startMid = null;
      }
      if (event.touches.length) return;

      panStart = null;
      viewport.classList.remove('is-local-zoom-panning');
      if (scale < 1.05) reset();

      const endedTouch = event.changedTouches?.[0];
      const duration = tapStart ? Date.now() - tapStart.time : Number.POSITIVE_INFINITY;
      const isTap = Boolean(endedTouch && tapStart && !moved && duration <= TAP_MAX_DURATION);
      tapStart = null;

      if (!isTap) {
        clearLastTap();
        return;
      }

      const tap = { clientX: endedTouch.clientX, clientY: endedTouch.clientY, time: Date.now() };
      const isDoubleTap = Boolean(
        lastTap
        && tap.time - lastTap.time <= DOUBLE_TAP_MS
        && Math.hypot(tap.clientX - lastTap.clientX, tap.clientY - lastTap.clientY) <= DOUBLE_TAP_DISTANCE
      );

      if (isDoubleTap) {
        event.preventDefault();
        suppressClickUntil = Date.now() + CLICK_SUPPRESS_MS;
        clearLastTap();
        zoomAt(tap.clientX, tap.clientY);
        return;
      }

      clearLastTap();
      lastTap = tap;
      prepareHighResolution(false);
      lastTapTimer = window.setTimeout(clearLastTap, DOUBLE_TAP_MS + 40);
    }, { passive: false });

    viewport.addEventListener('touchcancel', () => {
      tapStart = null;
      panStart = null;
      startDistance = 0;
      startMid = null;
      moved = true;
      clearLastTap();
      viewport.classList.remove('is-local-zoom-panning');
    }, { passive: true });

    viewport.addEventListener('dblclick', (event) => {
      if (Date.now() - lastTouchAt < TOUCH_DBLCLICK_GUARD) return;
      event.preventDefault();
      suppressClickUntil = Date.now() + CLICK_SUPPRESS_MS;
      zoomAt(event.clientX, event.clientY);
    });

    viewport.addEventListener('click', (event) => {
      if (Date.now() < suppressClickUntil || moved || scale > 1) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }, true);

    viewport._localZoomReset = reset;
  }

  const init = (scope = document) => {
    if (scope.matches?.(SELECTOR)) initViewport(scope);
    scope.querySelectorAll?.(SELECTOR).forEach(initViewport);
  };
  const resetAll = () => document.querySelectorAll('[data-local-zoom-ready="true"]').forEach((node) => node._localZoomReset?.());
  const observer = new MutationObserver((mutations) => mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => {
    if (node.nodeType === 1) init(node);
  })));
  const start = () => {
    init();
    observer.observe(document.documentElement, { childList: true, subtree: true });
  };
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('brilinton:gallery-updated', () => requestAnimationFrame(() => init()));
  document.addEventListener('royal-pop:model-change', () => {
    resetAll();
    requestAnimationFrame(() => init());
  });
  document.addEventListener('click', (event) => {
    if (event.target.closest?.('.slider-button,.thumbnail,[data-royal-pop-model-choice]')) resetAll();
  });
  window.addEventListener('pageshow', () => requestAnimationFrame(() => init()));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
